import type { MeshEnvironment } from '@/lib/env';
import { activeEnvironment } from '@/lib/environment';

export interface EnvironmentStyle {
  label: string;
  bg: string;
  border: string;
  dot: string;
  text: string;
  hairline: string;
}

/**
 * Environment styling.
 *
 * Production is red. That is a deliberate reading of the brief's "red exclusively
 * for danger" rule rather than a violation of it: production is the environment
 * where every action has real consequences, so red here carries exactly the
 * meaning it carries everywhere else in the product. Green → amber → red then
 * reads consistently as safe → caution → real consequences.
 */
const ENV_STYLES: Record<MeshEnvironment, EnvironmentStyle> = {
  production: {
    label: 'Production',
    bg: 'rgba(var(--err-rgb), 0.12)',
    border: 'rgba(var(--err-rgb), 0.45)',
    dot: 'var(--err)',
    text: 'var(--err-soft)',
    hairline: 'var(--err)',
  },
  staging: {
    label: 'Staging',
    bg: 'rgba(var(--warn-rgb), 0.12)',
    border: 'rgba(var(--warn-rgb), 0.42)',
    dot: 'var(--warn)',
    text: 'var(--warn)',
    hairline: 'var(--warn)',
  },
  dev: {
    label: 'Dev',
    bg: 'rgba(var(--ok-rgb), 0.1)',
    border: 'rgba(var(--ok-rgb), 0.38)',
    dot: 'var(--ok)',
    text: 'var(--ok)',
    hairline: 'rgba(var(--ok-rgb), 0.55)',
  },
};

export function environmentStyle(
  environment: MeshEnvironment = activeEnvironment,
): EnvironmentStyle {
  return ENV_STYLES[environment];
}
